import { Loader } from "lucide-react";
import { useEffect, useState } from "react";
import { getAllFormByUserIdService } from "../../../Services/AdminService/allFormsService";
import { useSelector } from "react-redux";

export default function DashboardStats() {
  const userId = useSelector((state) => state?.auth?.loggedInUser?._id);
  const [isLoading, setIsLoading] = useState(true);
  const [statsData, setStatsData] = useState({
    projects: 0,
    activeScaffolds: 0,
    scaffoldingItems: 0,
    scaffoldingWeight: 0,
    observations: 0,
    workTasks: 0,
    squareMeters: 0,
    cubicMeters: 0,
    lengthMeters: 0,
    heightMeters: 0,
    activeScaffoldsList: [],
    otherMeasurements: {},
  });

  useEffect(() => {
    if (!userId) return;
    getAllStatsDetails(userId);
  }, [userId]);

  const getAllStatsDetails = async (userId) => {
    try {
      setIsLoading(true);
      const response = await getAllFormByUserIdService(userId);
      if (response?.success) {
        const data = response.data;

        const projectsCount = data?.projects?.length || 0;
        const approvalFormsCount = data?.approvalForms?.length || 0;
        const observationsCount = data?.observations?.length || 0;
        const safeJobAnalysesCount = data?.safeJobAnalyses?.length || 0;

        let totalItems = 0;
        let totalWeight = 0;

        data.materialListWithProjects?.forEach((project) => {
          if (project.materialList && Array.isArray(project.materialList)) {
            totalItems += project.materialList.length;

            project.materialList.forEach((item) => {
              if (item.materialId && item.materialId.weight && item.quantity) {
                totalWeight += item.materialId.weight * item.quantity;
              }
            });
          }
        });

        const activeScaffolds =
          data.approvalForms?.filter(
            (scaffold) => scaffold.status === "active" && !scaffold.isDeleted
          ) || [];

        let lengthMeters = 0;
        let heightMeters = 0;
        let squareMeters = 0;
        let cubicMeters = 0;
        const otherMeasurements = {};

        activeScaffolds.forEach((scaffold) => {
          if (scaffold.scaffoldName && Array.isArray(scaffold.scaffoldName)) {
            scaffold.scaffoldName.forEach((scaffoldItem) => {
              if (scaffoldItem.measurements) {
                Object.keys(scaffoldItem.measurements).forEach(
                  (measurementType) => {
                    const measurements =
                      scaffoldItem.measurements[measurementType];
                    switch (measurementType) {
                      case "lm":
                        if (Array.isArray(measurements)) {
                          measurements.forEach((item) => {
                            lengthMeters += parseFloat(item.length) || 0;
                          });
                        }
                        break;

                      case "hm":
                        if (Array.isArray(measurements)) {
                          measurements.forEach((item) => {
                            heightMeters += parseFloat(item.height) || 0;
                          });
                        }
                        break;

                      case "m2":
                        if (Array.isArray(measurements)) {
                          measurements.forEach((item) => {
                            const calculatedValue =
                              parseFloat(item.length) * parseFloat(item.width);
                            squareMeters += parseFloat(calculatedValue) || 0;
                          });
                        }
                        break;

                      case "m3":
                        if (Array.isArray(measurements)) {
                          measurements.forEach((item) => {
                            const calculatedValue =
                              parseFloat(item.length) *
                              parseFloat(item.height) *
                              parseFloat(item.width);
                            cubicMeters += parseFloat(calculatedValue) || 0;
                          });
                        }
                        break;

                      default:
                        if (!otherMeasurements[measurementType]) {
                          otherMeasurements[measurementType] = 0;
                        }

                        if (Array.isArray(measurements)) {
                          measurements.forEach((item) => {
                            const values = Object.values(item);
                            for (const val of values) {
                              const numVal = parseFloat(val);
                              if (!isNaN(numVal)) {
                                otherMeasurements[measurementType] += numVal;
                                break;
                              }
                            }
                          });
                        } else if (typeof measurements === "object") {
                          // The measurement might be a direct value, not an array
                          const values = Object.values(measurements);
                          for (const val of values) {
                            const numVal = parseFloat(val);
                            if (!isNaN(numVal)) {
                              otherMeasurements[measurementType] += numVal;
                              break;
                            }
                          }
                        } else if (!isNaN(parseFloat(measurements))) {
                          // Direct numeric value
                          otherMeasurements[measurementType] +=
                            parseFloat(measurements);
                        }
                    }
                  }
                );
              }
            });
          }
        });

        if (squareMeters === 0 && lengthMeters > 0 && heightMeters > 0) {
          squareMeters = lengthMeters * heightMeters;
        }

        if (
          cubicMeters === 0 &&
          squareMeters > 0 &&
          otherMeasurements.depth > 0
        ) {
          cubicMeters = squareMeters * otherMeasurements.depth;
        }

        // Round all measurement values
        Object.keys(otherMeasurements).forEach((key) => {
          otherMeasurements[key] =
            Math.round(otherMeasurements[key] * 100) / 100;
        });
        setStatsData({
          projects: projectsCount,
          approvalForms: approvalFormsCount,
          workTasks: safeJobAnalysesCount,
          activeScaffolds: activeScaffolds.length,
          scaffoldingItems: totalItems,
          scaffoldingWeight: Math.round(totalWeight * 100) / 100,
          observations: observationsCount,
          squareMeters: Math.round(squareMeters * 100) / 100,
          cubicMeters: Math.round(cubicMeters * 100) / 100,
          lengthMeters: Math.round(lengthMeters * 100) / 100,
          heightMeters: Math.round(heightMeters * 100) / 100,
          activeScaffoldsList: activeScaffolds,
          otherMeasurements,
        });
      }
    } catch (error) {
      console.error("Error fetching stats data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="">
      <div className="space-y-6">
        {/* Project Overview */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Full Project Overview
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div className="p-4 border rounded-lg">
              <p className="text-sm font-medium text-gray-500 mb-1">
                All Projects
              </p>
              <p className="text-xl font-bold text-blue-600">
                {isLoading ? (
                  <Loader className="animate-spin" />
                ) : (
                  statsData.projects
                )}
              </p>
            </div>
            <div className="p-4 border rounded-lg">
              <p className="text-sm font-medium text-gray-500 mb-1">
                Approval Forms
              </p>
              <p className="text-xl font-bold text-blue-600">
                {isLoading ? (
                  <Loader className="animate-spin" />
                ) : (
                  statsData.approvalForms
                )}
              </p>
            </div>
            {/* <div className="p-4 border rounded-lg">
                <p className="text-sm font-medium text-gray-500 mb-1 ">
                  Scaffolding Weight
                </p>
                <div>
                  <p className="text-xl font-bold text-blue-600">
                    {isLoading ? (
                      <Loader className="animate-spin" />
                    ) : (
                      `${statsData.scaffoldingWeight} Kgs`
                    )}
                  </p>
                </div>
              </div> */}
            <div className="p-4 border rounded-lg">
              <p className="text-sm font-medium text-gray-500 mb-1">
                Observations
              </p>
              <p className="text-xl font-bold text-blue-600">
                {isLoading ? (
                  <Loader className="animate-spin" />
                ) : (
                  statsData.observations
                )}
              </p>
            </div>
            <div className="p-4 border rounded-lg">
              <p className="text-sm font-medium text-gray-500 mb-1">SJA</p>
              <p className="text-xl font-bold text-blue-600">
                {isLoading ? (
                  <Loader className="animate-spin" />
                ) : (
                  statsData.workTasks
                )}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div className="p-4 border rounded-lg">
              <p className="text-sm font-medium text-gray-500 mb-1">
                Square Meters (m²)
              </p>
              <p className="text-xl font-bold text-gray-800">
                {isLoading ? (
                  <Loader className="animate-spin" />
                ) : (
                  statsData.squareMeters
                )}
              </p>
            </div>
            <div className="p-4 border rounded-lg">
              <p className="text-sm font-medium text-gray-500 mb-1">
                Cubic Meters (m³)
              </p>
              <p className="text-xl font-bold text-gray-800">
                {isLoading ? (
                  <Loader className="animate-spin" />
                ) : (
                  statsData.cubicMeters
                )}
              </p>
            </div>
            <div className="p-4 border rounded-lg">
              <p className="text-sm font-medium text-gray-500 mb-1">
                Length Meters (lm)
              </p>
              <p className="text-xl font-bold text-gray-800">
                {isLoading ? (
                  <Loader className="animate-spin" />
                ) : (
                  statsData.lengthMeters
                )}
              </p>
            </div>
            <div className="p-4 border rounded-lg">
              <p className="text-sm font-medium text-gray-500 mb-1">
                Height Meters (hm)
              </p>
              <p className="text-xl font-bold text-gray-800">
                {isLoading ? (
                  <Loader className="animate-spin" />
                ) : (
                  statsData.heightMeters
                )}
              </p>
            </div>

            {!isLoading &&
              Object.keys(statsData.otherMeasurements).length > 0 && (
                <div className="mt-4">
                  <h3 className="text-md font-semibold text-gray-700 mb-2">
                    Other Measurements
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(statsData.otherMeasurements).map(
                      ([key, value]) => (
                        <div key={key} className="p-4 border rounded-lg">
                          <p className="text-sm font-medium text-gray-500 mb-1">
                            {key.charAt(0).toUpperCase() + key.slice(1)}
                          </p>
                          <p className="text-xl font-bold text-gray-800">
                            {value}
                          </p>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
          </div>

          {/*Materials andits weight */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div className="p-4 border rounded-lg">
              <p className="text-sm font-medium text-gray-500 mb-1">
                In company weight
              </p>
              <p className="text-xl font-bold text-gray-800">
                {isLoading ? (
                  <Loader className="animate-spin" />
                ) : (
                  "in progresss"
                )}
              </p>
            </div>
            <div className="p-4 border rounded-lg">
              <p className="text-sm font-medium text-gray-500 mb-1">
                On project Weight
              </p>
              <p className="text-xl font-bold text-gray-800">
                {isLoading ? (
                  <Loader className="animate-spin" />
                ) : (
                  "in progresss"
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
